import { Dialog, Transition } from '@headlessui/react'
import { Fragment, useState } from 'react'

import { CancelButton, SpinnerButton } from '@ui/components/core'
import { Notification, NotiticationsProps } from '@ui/components/core/Notification'
import { showToast } from '@ui/components/elements/ToastManager'
import { NotificationTypeEnum } from '@ui/interfaces'
import axios from '@ui/lib/api/axiosHelper'

interface CreateInstanceProps {
  fetchInstances: () => void
  modalState: boolean
  setModalClosed: () => void
  orgId: number
}

export const CreateInstanceModal = ({ fetchInstances, modalState, setModalClosed, orgId }: CreateInstanceProps) => {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [loading, setLoading] = useState(false)

  const [notification, setNotification] = useState<NotiticationsProps | null>(null)

  const onChangeName = (e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value)
  }

  const onChangeDescription = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDescription(e.target.value)
  }

  const onSubmit = (e: React.ChangeEvent<HTMLFormElement>) => {
    e.preventDefault()
    setLoading(true)
    setTimeout(addInstance, 1000)
  }

  const addInstance = async () => {
    try {
      await axios.post(`/v1/organizations/${orgId}/instances`, {
        instance: {
          name,
          description,
        },
      })

      showToast.success('Instance has been created')

      setLoading(false)
      fetchInstances()
      setModalClosed()
      setNotification(null)
    } catch (err: unknown) {
      setLoading(false)

      setNotification({ show: true, title: 'Failed to create instance key', type: NotificationTypeEnum.ERROR })

      console.error('Failed to create instance key: ', err)
    }
  }

  const clearInput = () => {
    setName('')
    setDescription('')
    setNotification(null)
  }

  return (
    <Transition.Root show={modalState} as={Fragment} afterLeave={clearInput}>
      <Dialog as="div" className="relative z-40" onClose={setModalClosed}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0">
          <div className="fixed inset-0 bg-gray-500/75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
              <Dialog.Panel className="relative overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-96 sm:p-6">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:text-left">
                    <Dialog.Title as="h3" className="text-base font-semibold leading-6 text-gray-900">
                      Create instance key
                    </Dialog.Title>
                  </div>
                </div>
                <form className="mt-5 sm:items-center" onSubmit={onSubmit}>
                  <div className="">
                    <label htmlFor="name" className="block text-sm font-medium leading-6 text-gray-900">
                      Name
                    </label>
                    <div className="mt-2">
                      <input
                        type="text"
                        name="name"
                        id="name"
                        onChange={onChangeName}
                        value={name}
                        disabled={loading}
                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                      />
                    </div>
                  </div>

                  <div className="mt-4">
                    <label htmlFor="name" className="block text-sm font-medium leading-6 text-gray-900">
                      Description
                    </label>
                    <div className="mt-2">
                      <input
                        type="text"
                        name="description"
                        id="description"
                        onChange={onChangeDescription}
                        value={description}
                        disabled={loading}
                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                      />
                    </div>
                  </div>

                  <div className="w-full">
                    <Notification
                      show={notification !== null}
                      type={NotificationTypeEnum.ERROR}
                      title={notification?.title || ''}
                      message={notification?.message}
                      richMessage={notification?.richMessage}
                      messageList={notification?.messageList}
                    />
                  </div>

                  <div className="mt-8 flex justify-end">
                    <SpinnerButton disabled={loading} loading={loading}></SpinnerButton>
                    <CancelButton onClick={setModalClosed} disabled={loading}></CancelButton>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  )
}
